/**
 * ## ItemsProviderSynchronizer.tsx ##
 * This file contains ItemsProviderSynchronizer component
 * @packageDocumentation
 */
import React from 'react';

import { NotificationAction } from '@common/typescript/objects/NotificationAction';
import { ItemsProviderContext, WithKey } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import { Notification } from '@common/typescript/objects/Notification';
import { BaseUser } from '@common/typescript/objects/BaseUser';
import {
	useAdvancedItemsProviderContext,
} from '@common/react/components/Core/AdvancedItemsProvider/AdvancedItemsProvider';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import {
	ItemsProviderWithStoreContext,
	useItemsProviderWithStoreContext,
} from '@common/react/components/Core/ItemsProviderWithStore/ItemsProviderWithStore';
import { defaultEqualPredicate, EqualPredicateDelegate } from '@common/react/objects/Core';
import { WithId } from '@common/typescript/objects/WithId';

/**
 * ItemsProviderSynchronizer component props
 *
 * @interface ItemsProviderProps
 * @typeParam T - T Any WithKey entity
 */
export interface ItemsProviderSynchronizerProps<T extends WithKey> {
	/**
	 * type of notification for filtering before call customHandler
	 */
	objectType: string;
	/**
	 * subtype of notification for filtering before call customHandler
	 */
	objectSubtype?: string;
	/**
	 * equalPredicate to find item at ItemsProvider for notification
	 * by default compare item ids
	 * @param a - item from itemsProvider
	 * @param b - item from notification
	 * @returns - if equals true, else false
	 */
	equalPredicate?: EqualPredicateDelegate<T>;
	/**
	 * handle notifications filtered by objectType and objectSubType
	 * by default handle notification data { id: number, entity: T }
	 * and apply it on AdvancedItemsProvider or ItemsProviderWithStore
	 * using equalPredicate method to find equals items from itemsProvider and notification
	 *
	 * @param notification - notification with data filtered by service flag, objectType and objectSubType
	 * @param context - AdvancedItemsProviderContext
	 * @param storeContext - ItemsProviderWithStoreContext
	 */
	// todo maybe we should rename it to something more appropriate
	customHandler?: (
		notification: Notification<BaseUser>,
		context: ItemsProviderContext<T>,
		storeContext: ItemsProviderWithStoreContext<T>,
	) => void;
	/**
	 * handle any types of notification without filtration
	 * in case of override excludes the call of customHandler
	 *
	 * @param notification - notification with any data
	 * @param context - ItemsProviderContext
	 */
	handle?: (notification: Notification<BaseUser>, context: ItemsProviderContext<T>) => void;
	/**
	 * handle init notification to setItems at ItemsProvider
	 *
	 * @param notification - notification with type 'Init' only
	 * @returns Array<T> - items
	 *
	 */
	getItemsFromInit?: (notification: Notification<BaseUser>) => Array<T>;
}

const defaultCustomHandler = <T extends WithId>(
	notification: Notification<BaseUser>,
	context: ItemsProviderContext<T>,
	storeContext: ItemsProviderWithStoreContext<T>,
	equalPredicate: EqualPredicateDelegate<T>,
) => {
	const handleNotification = storeContext?.actions?.handleNotification;
	const {
		state: {
			items, deleting, pagination,
		},
		actions: {
			addItem, updateItem, load,
		},
	} = context;

	const { data, action } = notification;

	if (data?.entity) {
		const itemData = { id: data.id, ...data.entity };

		const item = items.find((item) => equalPredicate(item, itemData));

		switch (action) {
			case NotificationAction.Add:
				!item && addItem(itemData, true, false);

				handleNotification?.({
					action: NotificationAction.Add,
					value: item,
				});

				break;
			case NotificationAction.Update:
				item ? updateItem(itemData) : addItem(itemData, true, false);

				handleNotification?.({
					action: NotificationAction.Update,
					value: item,
				});

				break;
			case NotificationAction.Delete:
				item && !deleting.current && load(pagination, { silent: true });

				handleNotification?.({
					action: NotificationAction.Delete,
					value: item.id,
				});

				break;
			default:
				break;
		}
	}
};

/**
 * ItemsProviderSynchronizer component
 * will subscribe on notifications and try to handle it with using outer ItemsProvider context
 *
 * @example inside AdvancedItemsProvider with default handler (notification with data: { id, entity } expected):
 * <AdvancedItemsProvider<T, TFilter>
 *   loadRequest="request"
 *   filterHandler={() => true}
 * >
 *   <ItemsProviderSynchronizer<T> objectType="notificationType" />
 * </AdvancedItemsProvider>
 *
 * @example inside AdvancedItemsProvider with customHandler, handle all notification with type 'type':
 * <AdvancedItemsProvider<T, TFilter>
 *   loadRequest="request"
 *   filterHandler={() => true}
 * >
 *   <ItemsProviderSynchronizer<T>
 *     objectType="notificationType"
 *     customHandler={(notification, context) => console.log(notification)}
 *   />
 * </AdvancedItemsProvider>
 *
 * @example inside ItemsProviderWithStore with getItemsFromInit to initialize redux data from init object:
 * <ItemsProviderWithStore<T, TFilter>
 *   loadRequest="request"
 *   storeName="storeName"
 *   defaultSort={['id', SortingDirection.Ascending]}
 *   filterHandler={() => false}
 * >
 *   <ItemsProviderSynchronizer<T>
 *     objectType="notificationType"
 *     getItemsFromInit={(notification) => notification.data.items}
 *   />
 * </ItemsProviderWithStore>
 *
 * @typeParam T - T Any {@link WithKey}
 * @param props - {@link ItemsProviderSynchronizerProps}
 * @returns React.Fragment
 */
const ItemsProviderSynchronizer = <T extends WithKey>(props: ItemsProviderSynchronizerProps<T>) => {
	const {
		equalPredicate = defaultEqualPredicate,
		customHandler = defaultCustomHandler,
		objectType,
		objectSubtype,
		handle = (
			notification: Notification<BaseUser>,
			context: ItemsProviderContext<T>,
			storeContext: ItemsProviderWithStoreContext<T>,
			equalPredicate: EqualPredicateDelegate<T>,
		) => {
			if (notification.data && notification.objectType === objectType && notification.service
				&& ((objectSubtype && notification.objectSubtype === objectSubtype)
					|| (!objectSubtype && !notification.objectSubtype))) {
				customHandler(notification, context, storeContext, equalPredicate);
			}
		},
		getItemsFromInit,
	} = props;

	const context = useAdvancedItemsProviderContext<T>();
	const storeContext = useItemsProviderWithStoreContext<T>();
	const { subscribe } = useApplicationContext();

	const {
		state: {
			items, loaders,
		},
		actions: {
			setItems,
		},
	} = context;

	const _handle = (notification: Notification<BaseUser>) => {
		if (notification.objectType === 'Init' && getItemsFromInit) {
			setItems(getItemsFromInit(notification));
		}

		handle(notification, context, storeContext, equalPredicate);
	};

	React.useEffect(subscribe(_handle), [items, loaders]);

	return <></>;
};

export default ItemsProviderSynchronizer;
